import { Link } from '@tanstack/react-router';
import MetricCard from '@/components/ui/MetricCard.tsx';
import { STATUS_TONE_COLORS } from '@/components/ui/statusTone.ts';
import { keyedSegmentValue } from '@/components/charts/breakdownAdapter.ts';
import { MoneyBreakdownDto } from '@/types/stats/BreakdownDto.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import {
  BALANCE_SEGMENT_REGISTRY,
  BalanceSegmentKey,
} from '@/features/dashboard/tours/types/finance/balanceSegments.utils.ts';
import { FinanceView } from '@/features/dashboard/tours/routes/tourFinanceView.ts';
import {
  barShare,
  formatShare,
} from '@/features/dashboard/tours/types/share.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';

interface TourPaymentsCardProps {
  balance: MoneyBreakdownDto;
  tourId: string;
  /** Dopisać walutę do tytułu — tylko gdy kart jest więcej niż jedna. */
  showCurrency: boolean;
}

/** Ile już wpłynęło z tego, czego wyjazd oczekuje, i co z tego jest po terminie. */
const TourPaymentsCard = ({
  balance,
  tourId,
  showCurrency,
}: TourPaymentsCardProps) => {
  const paid = keyedSegmentValue(balance, BalanceSegmentKey.PAID);
  const overdue = keyedSegmentValue(balance, BalanceSegmentKey.OVERDUE);

  return (
    <MetricCard
      title={
        showCurrency
          ? `Wpłaty · ${getCurrencyLabel(balance.currency)}`
          : 'Wpłaty'
      }
      value={formatPrice(paid, balance.currency)}
      unit={`z ${formatPrice(balance.total, balance.currency)}`}
      share={formatShare(paid, balance.total) ?? undefined}
      segments={[
        {
          id: 'paid',
          pct: barShare(paid, balance.total),
          color: STATUS_TONE_COLORS.success.dot,
        },
        {
          id: 'overdue',
          pct: barShare(overdue, balance.total),
          color: STATUS_TONE_COLORS.error.dot,
        },
      ]}
      footer={[
        {
          id: 'expected',
          label: 'Oczekiwane razem',
          value: formatPrice(balance.total, balance.currency),
        },
        {
          id: 'overdue',
          label: BALANCE_SEGMENT_REGISTRY[BalanceSegmentKey.OVERDUE].label,
          value: (
            <Link
              to="/app/tours/$tourId/finances"
              params={{ tourId }}
              search={{ view: FinanceView.PAYMENTS }}
              className="underline-offset-2 hover:underline"
              // Zaległość świeci tonem błędu dopiero wtedy, gdy jest czym
              // świecić — zero po terminie to dobra wiadomość.
              style={
                overdue > 0
                  ? { color: STATUS_TONE_COLORS.error.text }
                  : undefined
              }
            >
              {formatPrice(overdue, balance.currency)}
            </Link>
          ),
        },
      ]}
    />
  );
};

export default TourPaymentsCard;
